import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from './Navbar';
import axios from 'axios';
import {
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  SkipBack, SkipForward, Settings, Check, Lock, ChevronRight,
  ChevronLeft, RotateCcw, Clock, CheckCircle, PlayCircle,
  List, X, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format time helper
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const CoursePage = () => {
  const { user, isAuthenticated, hasAccess, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showPlaylist, setShowPlaylist] = useState(true);
  
  // Video player state
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [buffered, setBuffered] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  
  const controlsTimeout = useRef(null);
  const lastProgressUpdate = useRef(0);

  // Redirect unauthorized users
  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        // Not logged in - redirect to home
        navigate('/', { replace: true });
      } else if (!hasAccess) {
        // Logged in but hasn't paid - redirect to enrollment section
        navigate('/#enroll', { replace: true });
      }
    }
  }, [authLoading, isAuthenticated, hasAccess, navigate]);

  // Fetch modules
  useEffect(() => {
    // Only fetch if user has access
    if (!isAuthenticated || !hasAccess) return;
    
    const fetchModules = async () => {
      try {
        const response = await axios.get(`${API}/courses/modules`, { withCredentials: true });
        setModules(response.data);
        
        // Set first unlocked module
        const firstUnlocked = response.data.find(m => !m.is_locked);
        if (firstUnlocked) {
          setSelectedModule(firstUnlocked);
          setCurrentIndex(response.data.indexOf(firstUnlocked));
        }
      } catch (error) {
        console.error('Failed to fetch modules:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchModules();
  }, [isAuthenticated, hasAccess]);

  // Save progress
  const saveProgress = useCallback(async (time, completed = false) => {
    if (!selectedModule?.module_id || !isAuthenticated) return;
    
    if (!completed && Math.abs(time - lastProgressUpdate.current) < 10) return;
    lastProgressUpdate.current = time;
    
    try {
      await axios.post(`${API}/courses/progress`, {
        module_id: selectedModule.module_id,
        watched_seconds: Math.floor(time),
        is_completed: completed
      }, { withCredentials: true });
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  }, [selectedModule?.module_id, isAuthenticated]);

  // Video control functions
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  };

  const handleSeek = (e) => {
    if (!progressRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newTime = percent * duration;
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const skip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + seconds));
    }
  };

  const changePlaybackRate = (rate) => {
    setPlaybackRate(rate);
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
    setShowSettings(false);
  };

  const restart = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    clearTimeout(controlsTimeout.current);
    controlsTimeout.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  // Video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      saveProgress(video.currentTime);
    };
    const onLoadedMetadata = () => setDuration(video.duration);
    const onProgress = () => {
      if (video.buffered.length > 0) {
        setBuffered(video.buffered.end(video.buffered.length - 1));
      }
    };
    const onEnded = () => {
      setIsPlaying(false);
      saveProgress(video.duration, true);
      // Auto-play next module if available
      if (currentIndex < modules.length - 1) {
        const nextModule = modules[currentIndex + 1];
        if (!nextModule.is_locked) {
          setTimeout(() => {
            setSelectedModule(nextModule);
            setCurrentIndex(currentIndex + 1);
          }, 2000);
        }
      }
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('progress', onProgress);
    video.addEventListener('ended', onEnded);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('progress', onProgress);
      video.removeEventListener('ended', onEnded);
    };
  }, [saveProgress, currentIndex, modules]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT') return;
      
      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          skip(-10);
          break;
        case 'ArrowRight':
          skip(10);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setVolume(v => Math.min(1, v + 0.1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setVolume(v => Math.max(0, v - 0.1));
          break;
        case 'm':
          toggleMute();
          break;
        case 'f':
          toggleFullscreen();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const selectModule = (module, index) => {
    if (module.is_locked) return;
    setSelectedModule(module);
    setCurrentIndex(index);
    setCurrentTime(0);
    setIsPlaying(false);
  };

  // Get video source - Vimeo embed or sample video
  const getVideoSource = () => {
    if (selectedModule?.vimeo_video_id) {
      return null; // Will use iframe for Vimeo
    }
    return 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#d4a33a] animate-spin" />
      </div>
    );
  }

  // Redirect will happen via useEffect, show loading in meantime
  if (!isAuthenticated || !hasAccess) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-16 h-16 text-[#d4a33a] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Access Required</h2>
          <p className="text-gray-400 mb-4">
            {!isAuthenticated 
              ? 'Please sign in to access the course.'
              : 'Please complete your enrollment to access the course.'
            }
          </p>
          <Loader2 className="w-6 h-6 text-[#d4a33a] animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#d4a33a] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1117]">
      <Navbar />
      
      <div className="pt-20 pb-12">
        <div className="max-w-[1800px] mx-auto px-4">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {t('energyHealingCourse')}
              </h1>
              <p className="text-gray-400">
                {isAuthenticated && hasAccess 
                  ? `Welcome back, ${user?.name?.split(' ')[0]}! Continue your journey.`
                  : 'Preview available modules. Sign in to track your progress.'
                }
              </p>
            </div>
            <button
              onClick={() => setShowPlaylist(!showPlaylist)}
              className="flex items-center gap-2 px-4 py-2 bg-[#232836] text-white rounded-lg hover:bg-[#2a3142] transition-colors"
            >
              <List className="w-5 h-5" />
              {showPlaylist ? 'Hide Playlist' : 'Show Playlist'}
            </button>
          </div>

          <div className="flex gap-6">
            {/* Main Video Player */}
            <div className={`flex-1 ${showPlaylist ? '' : 'max-w-full'}`}>
              <div
                ref={containerRef}
                className="relative bg-black rounded-2xl overflow-hidden group"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => isPlaying && setShowControls(false)}
                style={{ boxShadow: '0 0 60px rgba(212, 163, 58, 0.2)' }}
              >
                {/* Video Element or Vimeo Embed */}
                {selectedModule?.vimeo_video_id ? (
                  <div className="aspect-video">
                    <iframe
                      src={`https://player.vimeo.com/video/${selectedModule.vimeo_video_id}?badge=0&autopause=0&player_id=0&app_id=58479`}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title={selectedModule.title}
                    />
                  </div>
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      src={getVideoSource()}
                      className="w-full aspect-video"
                      onClick={togglePlay}
                      poster="https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?w=1920&h=1080&fit=crop"
                    />

                    {/* Play Button Overlay */}
                    {!isPlaying && (
                      <div
                        className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/30"
                        onClick={togglePlay}
                      >
                        <div className="w-24 h-24 bg-[#d4a33a] rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-2xl"
                          style={{ boxShadow: '0 0 40px rgba(212, 163, 58, 0.5)' }}>
                          <Play className="w-12 h-12 text-white fill-white ml-2" />
                        </div>
                      </div>
                    )}

                    {/* Controls Overlay */}
                    <div
                      className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent p-6 transition-opacity duration-300 ${
                        showControls ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      {/* Progress Bar */}
                      <div
                        ref={progressRef}
                        className="relative h-2 bg-white/20 rounded-full cursor-pointer mb-4 group/progress"
                        onClick={handleSeek}
                      >
                        {/* Buffered */}
                        <div
                          className="absolute h-full bg-white/30 rounded-full"
                          style={{ width: `${(buffered / duration) * 100}%` }}
                        />
                        {/* Progress */}
                        <div
                          className="absolute h-full bg-[#d4a33a] rounded-full transition-all"
                          style={{ width: `${(currentTime / duration) * 100}%` }}
                        />
                        {/* Thumb */}
                        <div
                          className="absolute w-5 h-5 bg-[#d4a33a] rounded-full -top-1.5 -ml-2.5 opacity-0 group-hover/progress:opacity-100 transition-opacity shadow-lg"
                          style={{ left: `${(currentTime / duration) * 100}%` }}
                        />
                      </div>

                      {/* Controls Row */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Play/Pause */}
                          <button onClick={togglePlay} className="text-white hover:text-[#d4a33a] transition-colors">
                            {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
                          </button>

                          {/* Restart */}
                          <button onClick={restart} className="text-white hover:text-[#d4a33a] transition-colors">
                            <RotateCcw className="w-6 h-6" />
                          </button>

                          {/* Skip Back */}
                          <button onClick={() => skip(-10)} className="text-white hover:text-[#d4a33a] transition-colors relative">
                            <SkipBack className="w-6 h-6" />
                            <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-xs">10s</span>
                          </button>

                          {/* Skip Forward */}
                          <button onClick={() => skip(10)} className="text-white hover:text-[#d4a33a] transition-colors relative">
                            <SkipForward className="w-6 h-6" />
                            <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-xs">10s</span>
                          </button>

                          {/* Volume */}
                          <div 
                            className="flex items-center gap-2"
                            onMouseEnter={() => setShowVolumeSlider(true)}
                            onMouseLeave={() => setShowVolumeSlider(false)}
                          >
                            <button onClick={toggleMute} className="text-white hover:text-[#d4a33a] transition-colors">
                              {isMuted || volume === 0 ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${showVolumeSlider ? 'w-24' : 'w-0'}`}>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={isMuted ? 0 : volume}
                                onChange={handleVolumeChange}
                                className="w-full h-1.5 bg-white/30 rounded-full appearance-none cursor-pointer accent-[#d4a33a]"
                              />
                            </div>
                          </div>

                          {/* Time */}
                          <div className="flex items-center gap-2 text-white text-sm">
                            <Clock className="w-4 h-4" />
                            <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          {/* Settings */}
                          <div className="relative">
                            <button
                              onClick={() => setShowSettings(!showSettings)}
                              className="text-white hover:text-[#d4a33a] transition-colors"
                            >
                              <Settings className="w-6 h-6" />
                            </button>
                            {showSettings && (
                              <div className="absolute bottom-full right-0 mb-2 bg-[#232836] rounded-xl shadow-xl p-3 min-w-40 border border-gray-700">
                                <p className="text-gray-400 text-xs px-2 mb-2 uppercase tracking-wider">Playback Speed</p>
                                {[0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
                                  <button
                                    key={rate}
                                    onClick={() => changePlaybackRate(rate)}
                                    className={`w-full text-left px-3 py-2 text-sm rounded-lg flex items-center justify-between ${
                                      playbackRate === rate ? 'text-[#d4a33a] bg-[#d4a33a]/10' : 'text-white hover:bg-white/10'
                                    }`}
                                  >
                                    {rate}x
                                    {playbackRate === rate && <Check className="w-4 h-4" />}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Fullscreen */}
                          <button onClick={toggleFullscreen} className="text-white hover:text-[#d4a33a] transition-colors">
                            {isFullscreen ? <Minimize className="w-6 h-6" /> : <Maximize className="w-6 h-6" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Locked Overlay */}
                {selectedModule?.is_locked && (
                  <div className="absolute inset-0 bg-black/80 flex items-center justify-center backdrop-blur-sm">
                    <div className="text-center">
                      <Lock className="w-16 h-16 text-[#d4a33a] mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-white mb-2">Module Locked</h3>
                      <p className="text-gray-400 mb-4">Complete previous modules to unlock</p>
                      {!hasAccess && (
                        <button
                          onClick={() => navigate('/#enroll')}
                          className="bg-[#d4a33a] hover:bg-[#c49530] text-white px-6 py-3 rounded-lg font-medium"
                        >
                          Unlock Full Course
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Video Info */}
              <div className="mt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-[#d4a33a] text-sm font-medium">
                      Part {selectedModule?.part} • Module {currentIndex + 1}
                    </span>
                    <h2 className="text-2xl font-bold text-white mt-1">{selectedModule?.title}</h2>
                    <p className="text-gray-400 mt-2">{selectedModule?.description}</p>
                  </div>
                  {selectedModule?.progress?.is_completed && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Completed</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Playlist Sidebar */}
            {showPlaylist && (
              <div className="w-96 bg-[#1a1f2e] rounded-2xl border border-gray-800 overflow-hidden flex flex-col max-h-[calc(100vh-140px)]">
                <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Course Playlist</h3>
                    <p className="text-gray-400 text-sm">{modules.length} modules • 4 parts</p>
                  </div>
                  <button
                    onClick={() => setShowPlaylist(false)}
                    className="p-2 hover:bg-[#232836] rounded-lg text-gray-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {[1, 2, 3, 4].map(part => {
                    const partModules = modules.filter(m => m.part === part);
                    const partTitles = {
                      1: t('part1Title'),
                      2: t('part2Title'),
                      3: t('part3Title'),
                      4: t('part4Title')
                    };
                    
                    return (
                      <div key={part} className="border-b border-gray-800 last:border-0">
                        <div className="px-4 py-3 bg-[#232836]/50">
                          <span className="text-[#d4a33a] text-xs font-bold">PART {part}</span>
                          <h4 className="text-white font-medium text-sm">{partTitles[part]}</h4>
                        </div>
                        <div className="py-2">
                          {partModules.map((module, idx) => {
                            const globalIndex = modules.indexOf(module);
                            const isActive = module.module_id === selectedModule?.module_id;
                            const isLocked = module.is_locked;
                            const isCompleted = module.progress?.is_completed;
                            
                            return (
                              <button
                                key={module.module_id}
                                onClick={() => selectModule(module, globalIndex)}
                                disabled={isLocked}
                                className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                                  isActive
                                    ? 'bg-[#d4a33a]/20 border-l-4 border-[#d4a33a]'
                                    : isLocked
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-[#232836]'
                                }`}
                              >
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                  isCompleted ? 'bg-green-500' : isActive ? 'bg-[#d4a33a]' : 'bg-[#232836]'
                                }`}>
                                  {isLocked ? (
                                    <Lock className="w-4 h-4 text-white" />
                                  ) : isCompleted ? (
                                    <Check className="w-5 h-5 text-white" />
                                  ) : isActive ? (
                                    <PlayCircle className="w-5 h-5 text-white" />
                                  ) : (
                                    <span className="text-white text-sm font-bold">{globalIndex + 1}</span>
                                  )}
                                </div>
                                <div className="flex-1 text-left min-w-0">
                                  <p className={`font-medium text-sm truncate ${
                                    isActive ? 'text-[#d4a33a]' : 'text-white'
                                  }`}>
                                    {module.title}
                                  </p>
                                  {module.duration_seconds && (
                                    <p className="text-gray-500 text-xs mt-0.5">
                                      {formatTime(module.duration_seconds)}
                                    </p>
                                  )}
                                </div>
                                {isActive && !isLocked && (
                                  <ChevronRight className="w-4 h-4 text-[#d4a33a]" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePage;
